const SSE_LAST_ID_KEY = 'veltrix_admin_sse_last_id'
const DISMISSED_SYNC_KEY = 'veltrix_dismissed_sync_jobs'
const MAX_DISMISSED = 500

export function readPersistedLastId() {
  try {
    // Priorizar sessionStorage, usar localStorage apenas como fallback
    const raw = sessionStorage.getItem(SSE_LAST_ID_KEY) ?? localStorage.getItem(SSE_LAST_ID_KEY)
    const n = parseInt(raw ?? '', 10)
    return Number.isFinite(n) && n >= 0 ? n : 0
  } catch {
    return 0
  }
}

export function persistLastId(id) {
  const n = Number(id)
  if (!Number.isFinite(n) || n < 0) return
  try {
    // Priorizar sessionStorage para maior segurança (limpa ao fechar aba)
    sessionStorage.setItem(SSE_LAST_ID_KEY, String(n))
    // localStorage apenas como fallback
    if (typeof Storage !== 'undefined') {
      localStorage.setItem(SSE_LAST_ID_KEY, String(n))
    }
  } catch {
    /* ignore */
  }
}

export function resetAdminNotificationStreamCursor() {
  try {
    localStorage.removeItem(SSE_LAST_ID_KEY)
    sessionStorage.removeItem(SSE_LAST_ID_KEY)
    // Mantém DISMISSED_SYNC_KEY: o utilizador já viu/dispensou essas notificações.
  } catch {
    /* ignore */
  }
}

/** Chave estável para não reexibir notificações já vistas/dispensadas após limpar o sininho ou reconectar SSE. */
export function syncDismissKey(data) {
  if (!data || typeof data !== 'object') {
    return ''
  }

  if (data.kind === 'server_sync') {
    const jobId = String(data.job_id ?? '').trim()
    if (jobId !== '') {
      return `job:${jobId}`
    }
    const result = data.result && typeof data.result === 'object' ? data.result : {}
    return [
      'fp',
      data.server_id ?? '',
      data.state ?? '',
      result.eligible ?? '',
      result.partial ?? '',
      result.failed ?? '',
      result.ssh_sent ?? '',
      result.v2ray_sent ?? ''
    ].join(':')
  }

  if (data.kind === 'account_provision') {
    const jobId = String(data.job_id ?? '').trim()
    if (jobId !== '') {
      return `provision:job:${jobId}`
    }
    const summary = data.summary && typeof data.summary === 'object' ? data.summary : {}
    return [
      'provision:fp',
      data.account_id ?? '',
      data.username ?? '',
      data.state ?? '',
      summary.completed ?? '',
      summary.failed ?? ''
    ].join(':')
  }

  return ''
}

function loadDismissedSet() {
  try {
    // Priorizar sessionStorage
    const raw = sessionStorage.getItem(DISMISSED_SYNC_KEY) ?? localStorage.getItem(DISMISSED_SYNC_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? arr : [])
  } catch {
    return new Set()
  }
}

function saveDismissedSet(set) {
  try {
    const arr = [...set].slice(-MAX_DISMISSED)
    // Limitar dados armazenados para reduzir fingerprinting
    const limited = arr.slice(-100) // Máximo 100 itens
    sessionStorage.setItem(DISMISSED_SYNC_KEY, JSON.stringify(limited))
    // localStorage apenas se sessionStorage não disponível
    if (!sessionStorage.getItem(DISMISSED_SYNC_KEY) && typeof Storage !== 'undefined') {
      localStorage.setItem(DISMISSED_SYNC_KEY, JSON.stringify(limited))
    }
  } catch {
    /* ignore */
  }
}

export function isSyncNotificationDismissed(data) {
  const key = syncDismissKey(data)
  if (key === '') return false
  return loadDismissedSet().has(key)
}

export function dismissSyncNotification(data) {
  const key = syncDismissKey(data)
  if (key === '') return
  const set = loadDismissedSet()
  set.add(key)
  saveDismissedSet(set)
}

export function dismissSyncNotificationsFromList(notifications) {
  const set = loadDismissedSet()
  let changed = false
  for (const n of notifications || []) {
    const key = syncDismissKey(n?.data)
    if (key !== '' && !set.has(key)) {
      set.add(key)
      changed = true
    }
    if (typeof n?.sseEventId === 'number' && n.sseEventId > 0) {
      const current = readPersistedLastId()
      if (n.sseEventId > current) {
        persistLastId(n.sseEventId)
      }
    }
  }
  if (changed) {
    saveDismissedSet(set)
  }
}
